import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useAuth } from '../auth/AuthProvider';

/** Rest this long on a segment before fetching its frame. */
const SETTLE_MS = 200;

class ThumbnailUnavailable extends Error {}

function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      if (typeof r.result === 'string') resolve(r.result);
      else reject(new Error('unreadable image'));
    };
    r.onerror = () => {
      reject(r.error ?? new Error('unreadable image'));
    };
    r.readAsDataURL(blob);
  });
}

/** The segment id once the pointer has rested on it. */
function useSettled(id: string | null): string | null {
  const [settled, setSettled] = useState<string | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettled(id);
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [id]);
  return id === null ? null : settled;
}

/**
 * The first frame of the recorded segment under the pointer (ADR-043), shown
 * above the timeline. Frames never change: each is fetched once per session.
 * Decorative: the time tip on the bar already says where the pointer is.
 */
export function TimelineThumb({ segmentId, left }: { segmentId: string | null; left: number }) {
  const { accessToken } = useAuth();
  const id = useSettled(segmentId);
  const frame = useQuery({
    queryKey: ['recording-thumbnail', id],
    enabled: id !== null,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const token = accessToken();
      const res = await fetch(`/api/v1/recordings/${encodeURIComponent(id ?? '')}/thumbnail`, {
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 503) throw new ThumbnailUnavailable();
      if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
      return toDataUrl(await res.blob());
    },
  });

  if (segmentId === null || frame.isError) return null;
  // Only the frame of the segment under the pointer: an older one would mislead.
  const src = id === segmentId ? frame.data : undefined;
  return (
    <div
      aria-hidden="true"
      data-testid="timeline-thumb"
      className="pointer-events-none absolute bottom-full z-20 mb-2 w-40 -translate-x-1/2 overflow-hidden rounded-md border border-line-strong bg-surface-raised shadow-lg shadow-black/40"
      style={{ left: `${String(Math.min(Math.max(left, 8), 92))}%` }}
    >
      {src ? (
        <img src={src} alt="" className="block aspect-video w-full object-cover" />
      ) : (
        <div className="aspect-video w-full animate-pulse bg-surface-hover" />
      )}
    </div>
  );
}
